import type { QueryOptions } from "@tanstack/react-query"
import type { ExactPartial } from "@wagmi/core/internal"
import { Evaluate, getAddress } from "viem"
import {
  type GetVaultInterestRateModelInfoErrorType,
  type GetVaultInterestRateModelInfoParameters,
  type GetVaultInterestRateModelInfoReturnType,
  getVaultInterestRateModelInfo,
} from "../actions/getVaultInterestRateModelInfo"
import type { ExtendedConfig, ScopeKeyParameter } from "../types"
import { filterQueryOptions } from "../utils"

export type GetVaultInterestRateModelInfoOptions<
  config extends ExtendedConfig = ExtendedConfig,
> = Evaluate<
  ExactPartial<GetVaultInterestRateModelInfoParameters<config>> &
    ScopeKeyParameter
>

export function getVaultInterestRateModelInfoQueryKey<
  config extends ExtendedConfig = ExtendedConfig,
>(options: GetVaultInterestRateModelInfoOptions<config> = {}) {
  const { address, evcAddress, vaultLensAddress, ...parameters } = options
  return [
    "evcVaultInterestRateModel",
    filterQueryOptions({
      ...parameters,
      address: address && getAddress(address),
      evcAddress: evcAddress && getAddress(evcAddress),
      vaultLensAddress: vaultLensAddress && getAddress(vaultLensAddress),
    }),
  ] as const
}

export function getVaultInterestRateModelInfoQueryOptions<
  config extends ExtendedConfig,
>(config: config, options: GetVaultInterestRateModelInfoOptions<config>) {
  return {
    gcTime: 0,
    async queryFn({ queryKey }) {
      return getVaultInterestRateModelInfoQueryFn(config, queryKey[1])
    },
    queryKey: getVaultInterestRateModelInfoQueryKey(options),
  } satisfies QueryOptions<
    GetVaultInterestRateModelInfoQueryFnData,
    GetVaultInterestRateModelInfoErrorType,
    GetVaultInterestRateModelInfoData,
    GetVaultInterestRateModelInfoQueryKey
  >
}

export function getVaultInterestRateModelInfoQueryFn(
  config: ExtendedConfig,
  options: GetVaultInterestRateModelInfoOptions = {},
) {
  const { cash, borrows, ...parameters } = options
  return getVaultInterestRateModelInfo(config, {
    ...parameters,
    cash: cash || [],
    borrows: borrows || [],
  })
}

export type GetVaultInterestRateModelInfoQueryFnData =
  GetVaultInterestRateModelInfoReturnType

export type GetVaultInterestRateModelInfoData =
  GetVaultInterestRateModelInfoQueryFnData

export type GetVaultInterestRateModelInfoQueryKey = ReturnType<
  typeof getVaultInterestRateModelInfoQueryKey
>
